import { useMemo, useCallback } from 'react';
import { UserProject } from '../types/project.types';
import { HubRegionToRegion, Region } from '../types';
import {
	selectProjects,
	selectAuthenticationContext,
} from '../selectors/authenticationContext.selectors';
import { useSelector, useDispatch } from 'react-redux';
import {
	getProjectBySourceId,
	getProjects,
	injectProject,
} from '../actions/authenticationContext.actions';
import { AxiosError } from 'axios';
import { generateHost } from '../utils/generateHost';
import cookies from 'browser-cookies';

interface UseProjects {
	/**
	 * Whether the app is currently fetching projects or not
	 */
	isFetching: boolean;
	/**
	 * Whether the app has initialized projects or not
	 */
	isInitialized: boolean;
	/**
	 * Whether the app has initialized a project by their sourceId or not
	 */
	isProjectByIdInitialized: boolean;
	/**
	 * Fetches projects if not already initialized
	 */
	initializeProjects: () => void;
	/**
	 * Fetches a project by its sourceId if not already initialized
	 */
	initializeProjectBySourceId: () => void;
	/**
	 * The active project as defined by authenticationContext.defaultSite
	 */
	activeProject: UserProject | undefined;
	/**
	 * All projects apart from the active project
	 */
	alternativeProjects: UserProject[];
	/**
	 * Switches and reroutes to project based on projectSourceId
	 * @param projectSourceId
	 * @returns
	 */
	switchProject: (projectSourceId: string) => void;
	/**
	 * Fires project/inject action. Meant to cache data while user context is refetched.
	 * @param projectSourceId
	 * @returns
	 */
	injectProjectBySourceId: (projectSourceId: string) => void;
	/**
	 * List of all available projects for a user
	 */
	projects: UserProject[];
	/**
	 * Cursor for pagination
	 */
	cursor: string | null;
	/**
	 * Error if fetching projects failed
	 */
	error: AxiosError | null;
}

type ProjectSourceId = string;
type TenantId = string;

export const useProjects = (): UseProjects => {
	const dispatch = useDispatch();
	const {
		isFetching,
		isInitialized,
		cursor,
		list: projects,
		error,
		isProjectByIdInitialized,
	} = useSelector(selectProjects);
	const { defaultSiteProjectId } = useSelector(selectAuthenticationContext);

	const findActiveProject = useCallback(
		(project: UserProject) => {
			return project.id === defaultSiteProjectId;
		},
		[defaultSiteProjectId],
	);

	const findProjectBySourceId = useCallback(
		(projectSourceId: string) => (project: UserProject) =>
			project.sourceId === projectSourceId,
		[],
	);

	const switchProject = (projectSourceId: string) => {
		const project = projects.find(findProjectBySourceId(projectSourceId));
		if (project) {
			const protocol = window.location.host.includes('localhost')
				? 'http'
				: 'https';

			window.location.href = `${protocol}://${
				generateHost({
					region: HubRegionToRegion[project.hubRegion] || Region.US,
				}) + `/projects/${project.sourceId}`
			}`;
		} else {
			console.warn('project with matching id does not exist');
		}
	};

	const injectProjectBySourceId = useCallback(
		(projectSourceId: string) => {
			const project = projects.find(
				findProjectBySourceId(projectSourceId),
			);
			if (!project) return;

			dispatch(injectProject(project));
		},
		[projects.length],
	);

	const initializeProjects = () => {
		const token = cookies.get('auth0.user.token');
		// Ignore initialization attempts while fetching
		if (isInitialized || isFetching) return;
		// Wait to make request with token
		if (!token) {
			setTimeout(() => {
				initializeProjects();
			}, 200);
		} else {
			dispatch(getProjects({ initializing: true }));
		}
	};

	const initializeProjectBySourceId = () => {
		if (isProjectByIdInitialized || isFetching) return;
		// Wait if projects or context are not initialized
		if (!isInitialized || !defaultSiteProjectId) {
			setTimeout(() => {
				initializeProjectBySourceId();
			}, 200);
		} else {
			const projectSourceId =
				defaultSiteProjectId.split('project:').pop() || '';
			const existingProject = projects.find(
				findProjectBySourceId(projectSourceId),
			);
			if (!existingProject) {
				dispatch(getProjectBySourceId(projectSourceId));
			}
		}
	};

	const activeProject: UserProject | undefined = useMemo(() => {
		let project = projects.find(findActiveProject);
		if (!project && isInitialized && isProjectByIdInitialized) {
			project = projects[0];
		}
		return project;
	}, [
		findActiveProject,
		projects.length,
		defaultSiteProjectId,
		isInitialized,
		isProjectByIdInitialized,
	]);

	const alternativeProjects: UserProject[] = projects.filter(
		project => project.id !== defaultSiteProjectId,
	);

	return {
		isFetching,
		isInitialized,
		isProjectByIdInitialized,
		injectProjectBySourceId,
		initializeProjects,
		initializeProjectBySourceId,
		activeProject,
		alternativeProjects,
		switchProject,
		projects,
		cursor,
		error,
	};
};
